import React, { useState, useEffect } from "react";
import { Modal, Tag, Button } from "antd";
import { ClockCircleOutlined, CheckCircleOutlined, ShoppingOutlined, CarOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { roundDisplayPrice } from "../../utils/priceHelpers";

const getRestaurantValue = (order) => {
  if (!order) return 0;
  if (order.restaurantAmount !== undefined && order.restaurantAmount !== null && parseFloat(order.restaurantAmount) > 0) {
    return parseFloat(order.restaurantAmount);
  }
  if (order.restaurantTotal !== undefined && order.restaurantTotal !== null && parseFloat(order.restaurantTotal) > 0) {
    return parseFloat(order.restaurantTotal);
  }
  if (order.subtotal !== undefined && order.subtotal !== null && parseFloat(order.subtotal) > 0) {
    return parseFloat(order.subtotal);
  }
  const items = order.orderItems || order.items || [];
  if (items.length > 0) {
    return items.reduce((sum, item) => {
      const itemTotal = parseFloat(item.price || 0) * (item.quantity || 1);
      const containerCost = parseFloat(item.menuItem?.containerCost || item.containerCost || 0);
      const portionsPerPack = item.menuItem?.portionsPerPack || item.portionsPerPack || 1;
      const numContainers = containerCost > 0 ? Math.ceil((item.quantity || 1) / portionsPerPack) : 0;
      return sum + itemTotal + (containerCost * numContainers);
    }, 0);
  }
  return parseFloat(order.total_amount || order.totalAmount || 0);
};

const RestaurantPendingModal = ({ visible, onClose, activeOrders = [], onViewOrder, onRefresh }) => {
  const handleItemViewOrder = (order) => {
    if (typeof onRefresh === "function") {
      onRefresh();
    }
    if (onClose) onClose();
    if (onViewOrder) onViewOrder(order);
  };

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      if (typeof onRefresh === "function") {
        onRefresh();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [visible, onRefresh]);

  const safeOrders = Array.isArray(activeOrders)
    ? activeOrders
    : Array.isArray(activeOrders?.orders)
      ? activeOrders.orders
      : [];

  const ordersToAccept = safeOrders.filter(
    (o) => o.status === "WAITING" || o.status === "pending"
  );
  const ordersToConfirmReady = safeOrders.filter(
    (o) => o.status === "ACCEPTED" || o.status === "confirmed"
  );
  const ordersForCollection = safeOrders.filter(
    (o) => o.status === "READY" || o.delivery_opt === "pickup"
  );
  const ordersInTransit = safeOrders.filter(
    (o) => (o.status === "IN_TRANSIT" || o.delivery_status === "in_transit") && o.deliveryType !== "PICKUP"
  );

  const availableTabs = [
    { key: "accept", label: "To Accept", count: ordersToAccept.length, icon: <ClockCircleOutlined /> },
    { key: "ready", label: "Confirm Ready", count: ordersToConfirmReady.length, icon: <CheckCircleOutlined /> },
    { key: "collection", label: "For Collection", count: ordersForCollection.length, icon: <ShoppingOutlined /> },
    { key: "in-transit", label: "In Transits", count: ordersInTransit.length, icon: <CarOutlined /> },
  ].filter((t) => t.count > 0);

  const [activeTab, setActiveTab] = useState(() => availableTabs[0]?.key || "accept");

  useEffect(() => {
    if (availableTabs.length > 0) {
      if (!availableTabs.some((t) => t.key === activeTab)) {
        setActiveTab(availableTabs[0].key);
      }
    }
  }, [availableTabs, activeTab]);

  useEffect(() => {
    if (visible && availableTabs.length === 0) {
      if (onClose) onClose();
    }
  }, [visible, availableTabs.length]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <ClockCircleOutlined className="text-gray-800 text-xl" />
          <span className="font-gilroyBold text-lg text-gray-900">Restaurant Pending Activities</span>
        </div>
      }
      open={visible && availableTabs.length > 0}
      onCancel={onClose}
      centered
      footer={[
        <Button key="close" type="primary" onClick={onClose} className="bg-black hover:bg-gray-800 text-white border-none px-5 rounded-xl h-10 text-[14px] font-gilroyBold">
          Close
        </Button>,
      ]}
      width="92%"
      style={{ maxWidth: "520px" }}
      bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
    >
      <div className="pt-1 space-y-4">
        {availableTabs.length > 0 && (
          <div
            className="flex gap-2 border-b border-gray-200 pb-3 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {availableTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 rounded-xl text-[13px] sm:text-[14px] font-gilroyBold whitespace-nowrap transition-all flex items-center gap-1.5 shrink-0 ${
                  activeTab === tab.key
                    ? "bg-black text-white border border-black"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.icon}
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        )}

        {activeTab === "accept" && (
          <div className="space-y-4">
            {ordersToAccept.length > 0 ? (
              ordersToAccept.map((order, idx) => (
                <div key={idx} className="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
                    <span className="text-[13px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      ORDER CODE / REF
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(order)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>
                  <div className="font-gilroyBold text-3xl sm:text-4xl text-black font-black tracking-widest my-2 py-1 break-all">
                    {order.orderCode || order.transaction_ref || order.id || "N/A"}
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-[14px] pt-1">
                    <div>
                      <span className="text-gray-500 font-gilroyBold">Items: </span>
                      <span className="font-gilroyBold text-gray-900">{order.total_items || order.items?.length || 1}</span>
                    </div>
                    <div className="text-right sm:text-left">
                      <span className="text-gray-500 font-gilroyBold">Total: </span>
                      <span className="font-gilroyBold text-gray-900">
                        <NumberFormat
                          thousandSeparator={true}
                          prefix={"₦"}
                          className="numeric"
                          displayType={"text"}
                          value={roundDisplayPrice(getRestaurantValue(order))}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500 text-[14px] font-gilroyBold">No orders waiting for acceptance.</div>
            )}
          </div>
        )}

        {activeTab === "ready" && (
          <div className="space-y-4">
            {ordersToConfirmReady.length > 0 ? (
              ordersToConfirmReady.map((order, idx) => (
                <div key={idx} className="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
                    <span className="text-[13px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      CONFIRM READY PREPARATION
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(order)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>
                  <div className="font-gilroyBold text-3xl sm:text-4xl text-black font-black tracking-widest my-2 py-1 break-all">
                    {order.orderCode || order.transaction_ref || order.id || "N/A"}
                  </div>
                  <div className="text-[14px] text-gray-700 pt-1">
                    Order is currently being prepared. Confirm ready when items are packed.
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500 text-[14px] font-gilroyBold">No orders waiting to confirm ready.</div>
            )}
          </div>
        )}

        {activeTab === "collection" && (
          <div className="space-y-4">
            {ordersForCollection.length > 0 ? (
              ordersForCollection.map((order, idx) => (
                <div key={idx} className="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
                    <span className="text-[13px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      READY FOR COLLECTION
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(order)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>
                  <div className="font-gilroyBold text-3xl sm:text-4xl text-black font-black tracking-widest my-2 py-1 break-all">
                    {order.orderCode || order.transaction_ref || order.id || "N/A"}
                  </div>
                  <div className="text-[14px] text-gray-700 pt-1">
                    Order is ready for buyer or rider collection.
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500 text-[14px] font-gilroyBold">No orders ready for collection.</div>
            )}
          </div>
        )}

        {activeTab === "in-transit" && (
          <div className="space-y-4">
            {ordersInTransit.length > 0 ? (
              ordersInTransit.map((order, idx) => (
                <div key={idx} className="p-4 sm:p-5 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-3">
                  <div className="flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
                    <span className="text-[13px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      DELIVERY IN TRANSIT
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(order)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>
                  <div className="font-gilroyBold text-3xl sm:text-4xl text-black font-black tracking-widest my-2 py-1 break-all">
                    {order.orderCode || order.transaction_ref || order.id || "N/A"}
                  </div>
                  <div className="text-[14px] text-gray-700 pt-1">
                    Delivery rider is currently en route to customer location.
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500 text-[14px] font-gilroyBold">No delivery orders currently in-transit.</div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RestaurantPendingModal;
